import { Link } from 'react-router-dom';
import {
  Zap,
  BarChart3,
  Sparkles,
  Shield,
  ArrowRight,
  Check,
  Users,
  TrendingUp,
  FileText,
  Repeat2,
  Gift,
  Lock,
} from 'lucide-react';

const mainFeatures = [
  {
    icon: BarChart3,
    title: 'Analitiche Avanzate',
    desc: 'Visualizza i tuoi pattern di spesa con grafici interattivi a 12 mesi. Scopri esattamente dove vanno i tuoi soldi.',
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
  },
  {
    icon: Sparkles,
    title: 'Categorizzazione AI',
    desc: 'L\'IA classifica automaticamente ogni spesa, ma tu rimani sempre in controllo. Impara dai tuoi pattern istantaneamente.',
    color: 'text-brand-400',
    bg: 'bg-brand-500/10',
  },
  {
    icon: Repeat2,
    title: 'Spese Ricorrenti',
    desc: 'Imposta una volta le bollette ricorrenti e gli abbonamenti. Non dimenticare mai un pagamento.',
    color: 'text-purple-400',
    bg: 'bg-purple-500/10',
  },
  {
    icon: TrendingUp,
    title: 'Tracciamento Entrate',
    desc: 'Traccia più fonti di reddito insieme alle spese per un quadro finanziario completo.',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
  },
  {
    icon: FileText,
    title: 'Importa Busta Paga',
    desc: 'Carica le buste paga direttamente. L\'IA estrae automaticamente reddito e tasse.',
    color: 'text-orange-400',
    bg: 'bg-orange-500/10',
  },
  {
    icon: Shield,
    title: 'Sicurezza Aziendale',
    desc: 'Crittografia a livello bancario con sicurezza a livello di riga. Solo tu vedi i tuoi dati finanziari.',
    color: 'text-rose-400',
    bg: 'bg-rose-500/10',
  },
];

const pricingPlans = [
  {
    name: 'Gratuito',
    price: '€0',
    period: 'per sempre',
    trial: 'Sempre gratis - nessuna carta di credito',
    features: [
      'Fino a 50 spese/mese',
      'Analitiche di base (vista 12 mesi)',
      'Tutte le 9 categorie di spesa',
      'Esportazione CSV e PDF',
      'Tracciamento entrate',
      'Supporto email',
    ],
    cta: 'Inizia Gratis',
    href: '/register',
    highlighted: false,
  },
  {
    name: 'Premium Mensile',
    price: '€5',
    period: 'al mese',
    trial: 'Prova gratuita 7 giorni',
    features: [
      '✨ Tutto il piano Gratuito, più:',
      'Spese illimitate',
      'Categorizzazione AI',
      'Gestione spese ricorrenti',
      'Analitiche e insight avanzati',
      'Importazione busta paga',
      'Supporto email prioritario',
      'Bonus referral: +7 giorni per invito',
    ],
    cta: 'Prova Gratis',
    href: '/register',
    highlighted: true,
  },
  {
    name: 'Premium Annuale',
    price: '€40',
    originalPrice: '€60',
    period: 'all\'anno',
    features: [
      '✨ Tutto il piano Gratuito, più:',
      'Spese illimitate',
      'Categorizzazione AI',
      'Gestione spese ricorrenti',
      'Analitiche e insight avanzati',
      'Importazione busta paga',
      'Supporto email prioritario',
      'Bonus referral: +7 giorni per invito',
    ],
    cta: 'Prova Gratis',
    href: '/register',
    highlighted: false,
  },
];

const referralBenefits = [
  {
    icon: Gift,
    title: '+7 Giorni Prova',
    desc: 'Tu e il tuo amico ottenete +7 giorni di accesso premium',
  },
  {
    icon: Users,
    title: 'Condivisione Facile',
    desc: 'Link di invito con un click. I tuoi amici si registrano istantaneamente.',
  },
  {
    icon: Zap,
    title: 'Referral Illimitati',
    desc: 'Nessun limite di referral. Continua a invitare, continua a guadagnare giorni di prova.',
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Navigazione */}
      <nav className="border-b border-slate-800/60 backdrop-blur-sm bg-slate-950/80 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-brand-500 to-violet-600 flex items-center justify-center">
              <Zap size={14} className="text-white" />
            </div>
            <span className="text-lg font-bold gradient-text">BudgetAI</span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="text-sm text-slate-400 hover:text-white transition-colors px-3 py-1.5"
            >
              Accedi
            </Link>
            <Link to="/register" className="btn-primary text-sm">
              Inizia
            </Link>
          </div>
        </div>
      </nav>

      {/* Sezione Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-24 pb-20 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-500/10 border border-brand-500/20 text-xs font-medium text-brand-400 mb-8">
          <Sparkles size={12} />
          Piattaforma di Finanza Personale Alimentata da AI
        </div>

        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight mb-6">
          Ti Aiutiamo a Controllare{' '}
          <span className="gradient-text">i Tuoi Soldi</span>
          <br />
          con l'IA
        </h1>

        <p className="text-xl text-slate-400 max-w-3xl mx-auto mb-12 leading-relaxed">
          Basta con i fogli di calcolo. BudgetAI ti assiste nel tracciamento spese, nella visualizzazione dei tuoi pattern finanziari e nell'automazione delle cose noiose. Tu rimani sempre in controllo. Ottieni una visione completa del tuo reddito, spese e bollette ricorrenti. Tutto in un unico dashboard bellissimo e semplice.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
          <Link
            to="/register"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 text-white font-semibold text-lg transition-all active:scale-[0.98] shadow-lg shadow-brand-500/20"
          >
            Inizia Prova Gratuita
            <ArrowRight size={20} />
          </Link>
          <Link
            to="/login"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 font-semibold text-lg transition-all active:scale-[0.98]"
          >
            Accedi
          </Link>
        </div>

        <p className="text-sm text-slate-500">
          ✨ Sempre gratuito • Premium da €5/mese • Prova 7 giorni sui piani a pagamento
        </p>
      </section>

      {/* Griglia Feature Principali */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20 border-t border-slate-800">
        <h2 className="text-4xl font-bold text-center mb-4">
          Tutto quello che serve per <span className="gradient-text">controllare</span> le tue finanze
        </h2>
        <p className="text-center text-slate-400 mb-16 max-w-2xl mx-auto">
          Dal tracciamento all'automazione, dall'analisi agli insight. BudgetAI ha ogni strumento
          per aiutarti a costruire abitudini finanziarie migliori.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {mainFeatures.map(f => (
            <div key={f.title} className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-brand-500/20 to-violet-500/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity blur-xl" />
              <div className="relative card p-8 h-full">
                <div className={`w-12 h-12 rounded-xl ${f.bg} flex items-center justify-center mb-4`}>
                  <f.icon size={24} className={f.color} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{f.title}</h3>
                <p className="text-slate-400 leading-relaxed">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Sezione Referral */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-20 border-t border-slate-800">
        <div className="bg-gradient-to-r from-brand-900/40 to-violet-900/40 border border-brand-500/20 rounded-3xl p-12">
          <h2 className="text-3xl font-bold text-center mb-4">
            Invita Amici, Guadagna Giorni di Prova
          </h2>
          <p className="text-center text-slate-300 mb-12 max-w-2xl mx-auto">
            Condividi l'amore. Ogni volta che un amico si iscrive dal tuo link di referral, voi entrambi ottenete +7
            giorni di accesso premium. Nessun limite, continua a guadagnare!
          </p>

          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            {referralBenefits.map(benefit => (
              <div key={benefit.title} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-brand-500/20 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon size={24} className="text-brand-400" />
                </div>
                <h3 className="font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-sm text-slate-300">{benefit.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-semibold transition-all"
            >
              Ottieni il Tuo Link di Referral
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Sezione Tariffe */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20 border-t border-slate-800">
        <h2 className="text-4xl font-bold text-center mb-4">Tariffe per tutti</h2>
        <p className="text-center text-slate-400 mb-16 max-w-2xl mx-auto">
          Inizia gratis per sempre. Aggiorna quando ne hai bisogno. Cancella quando vuoi. Nessuna sorpresa.
        </p>

        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {pricingPlans.map(plan => (
            <div
              key={plan.name}
              className={[
                'rounded-2xl p-8 flex flex-col gap-6 relative',
                plan.highlighted
                  ? 'bg-gradient-to-b from-brand-900/60 to-slate-900 border-2 border-brand-500/60 shadow-2xl shadow-brand-500/10 ring-1 ring-brand-500/20'
                  : 'card',
              ].join(' ')}
            >
              {plan.highlighted && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-brand-500 text-xs font-bold text-white">
                    Più Popolare 🚀
                  </span>
                </div>
              )}

              <div>
                <p className="text-sm text-slate-400 mb-2">{plan.name}</p>
                <div className="flex items-baseline gap-2 mb-2">
                  {plan.originalPrice && (
                    <span className="text-xl text-slate-500 line-through">{plan.originalPrice}</span>
                  )}
                  <span className="text-4xl font-extrabold text-white">{plan.price}</span>
                  <span className="text-slate-400">{plan.period}</span>
                </div>
                <p className="text-sm text-emerald-400 font-medium">{plan.trial}</p>
              </div>

              <ul className="space-y-3 flex-1">
                {plan.features.map(f => (
                  <li key={f} className="flex items-start gap-3 text-sm text-slate-200">
                    <Check size={18} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Link
                to={plan.href}
                className={[
                  'block text-center py-3 px-4 rounded-xl font-semibold transition-all active:scale-[0.98]',
                  plan.highlighted
                    ? 'bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 text-white shadow-lg shadow-brand-500/20'
                    : 'bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700',
                ].join(' ')}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>

        <p className="text-center text-slate-500 text-sm mt-12">
          Pagamento sicuro con Stripe • Cancella quando vuoi
        </p>
      </section>

      {/* Sezione Sicurezza */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-20 border-t border-slate-800 text-center">
        <h2 className="text-3xl font-bold mb-8">
          I tuoi soldi, <span className="gradient-text">completamente al sicuro</span>
        </h2>

        <div className="grid sm:grid-cols-2 gap-6 mb-12">
          <div className="flex items-start gap-4">
            <Lock size={24} className="text-emerald-400 mt-1 flex-shrink-0" />
            <div className="text-left">
              <h3 className="font-semibold text-white mb-2">Crittografia End-to-End</h3>
              <p className="text-slate-400 text-sm">
                Crittografia SSL a livello bancario per tutti i dati in transito e a riposo.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <Shield size={24} className="text-blue-400 mt-1 flex-shrink-0" />
            <div className="text-left">
              <h3 className="font-semibold text-white mb-2">Sicurezza a Livello di Riga</h3>
              <p className="text-slate-400 text-sm">
                RLS gestito dal database. Solo tu puoi accedere ai tuoi dati, punto.
              </p>
            </div>
          </div>
        </div>

        <p className="text-sm text-slate-500">
          Costruito con architettura zero-knowledge. I tuoi dati sono tuoi.
        </p>
      </section>

      {/* Sezione CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-20 text-center border-t border-slate-800">
        <h2 className="text-3xl sm:text-4xl font-bold mb-6">
          Pronto a prendere il controllo delle tue finanze?
        </h2>
        <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
          Unisciti a migliaia di utenti che stanno semplificando la loro vita finanziaria con BudgetAI.
          Prova gratis per 7 giorni sul premium.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/register"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 text-white font-semibold text-lg transition-all shadow-lg shadow-brand-500/20"
          >
            Inizia Prova Gratuita
            <ArrowRight size={20} />
          </Link>
          <a
            href="#faq"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 font-semibold text-lg transition-all"
          >
            Scopri di Più
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 mt-20 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-8">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-brand-500 to-violet-600 flex items-center justify-center">
                <Zap size={14} className="text-white" />
              </div>
              <span className="text-lg font-bold gradient-text">BudgetAI</span>
            </div>
            <div className="flex items-center gap-8 text-sm text-slate-400">
              <Link to="/privacy" className="hover:text-white transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-white transition-colors">
                Termini
              </Link>
              <a href="mailto:support@budgetai.app" className="hover:text-white transition-colors">
                Supporto
              </a>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 text-center text-sm text-slate-500">
            <p>© {new Date().getFullYear()} BudgetAI. Creato con ❤️ usando React, Supabase e Stripe.</p>
            <p className="mt-2">
              Sicuro • Privato • Semplice • Aperto al feedback
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
